import React, {FC, memo, useState} from "react";
import {FileZipFilled, FileZipOutlined, InfoCircleOutlined, MoreOutlined} from "@ant-design/icons";
import {blue, grey, red} from "@ant-design/colors";
import {
    Course,
    GetBookingsOverviewOptimizedDocument,
    GetCoursesWithPaginationDocument,
    GetUserSettingDocument,
    useGetUserSettingQuery,
    useUpdateMultipleCoursesArchivedMutation,
    useUpdateShowArchivedCoursesMutation,
} from "../../../generated/graphql";
import Button from "../../../views/Button/Button";
import i18n from "../../../services/i18n";
import {Switch, Tooltip} from "antd";
import css from "./CourseListShowMoreMenu.less";
import Spin from "../../../views/Spin/Spin";
import DraggableModal from "../../../views/DraggableModal/DraggableModal";
import classNames from "classnames";

type CourseListShowMoreMenuProps = {
    courseIds?: Array<Course["id"]>;
    refetch?: () => void;
};

const CourseListShowMoreMenu: FC<CourseListShowMoreMenuProps> = ({courseIds, refetch}) => {
    const [menuOpen, setMenuOpen] = useState<boolean>(false);

    const [updateMultipleCoursesArchived, {loading: updatingMultipleCoursesArchived}] = useUpdateMultipleCoursesArchivedMutation({
        refetchQueries: ["GetCoursesWithPagination", {query: GetCoursesWithPaginationDocument}],
    });

    const ArchiveMultipleCourses = () => {
        const archiveButtonStyle: React.CSSProperties = {borderColor: blue[4], color: blue[6]};
        const unarchiveButtonStyle: React.CSSProperties = {borderColor: red[2], color: red[3]};

        if (courseIds && courseIds.length > 0) {
            return (
                <div style={{display: "flex", alignItems: "center", gap: 5}}>
                    <Button
                        type="default"
                        style={unarchiveButtonStyle}
                        size="small"
                        onClick={() => updateMultipleCoursesArchived({variables: {courseIds, isArchived: false}})}
                        disabled={updatingMultipleCoursesArchived}
                    >
                        {"Auswahl Dearchivieren"}
                    </Button>
                    <Button
                        type="default"
                        style={archiveButtonStyle}
                        size="small"
                        onClick={() => updateMultipleCoursesArchived({variables: {courseIds, isArchived: true}})}
                        disabled={updatingMultipleCoursesArchived}
                        icon={<FileZipFilled style={{color: grey[1], marginRight: -4}} />}
                    >
                        {"Auswahl Archivieren"}
                    </Button>
                </div>
            );
        }

        return null;
    };

    const ShowArchivedSwitcher = () => {
        const [updateShowArchivedCourses, {loading: updatingShowArchivedCourses}] = useUpdateShowArchivedCoursesMutation({
            // Summenzeile im Zahlungsverkehr per Name: nur aktive Abfrage, mit ihren Variablen
            refetchQueries: [{query: GetCoursesWithPaginationDocument}, {query: GetUserSettingDocument}, {query: GetBookingsOverviewOptimizedDocument}, "GetScheduledPaymentsSummary"],
            onCompleted: () => {
                // Manueller refetch nach erfolgreicher Mutation
                if (refetch) {
                    refetch();
                }
            },
        });
        const {data, loading, error} = useGetUserSettingQuery();
        const userSetting = data?.userSettings;

        if (error) return <div>{`Error: ${error.message}`}</div>;

        const handleChange = (checked: boolean) => {
            console.log("checked: ", checked);
            console.log("userSetting: ", userSetting);
            updateShowArchivedCourses({variables: {showArchivedCourses: checked}});
        };

        if (loading || updatingShowArchivedCourses) return <Spin />;

        return (
            <div>
                <Switch checkedChildren="Archivierte werden angezeigt" unCheckedChildren="Archivierte sind ausgeblendet" checked={userSetting?.showArchivedCourses} onChange={handleChange} />
            </div>
        );
    };

    const DescripitionModal = () => {
        const [descripitionModalOpen, setDescripitionModalOpen] = useState<boolean>(false);

        const title = (
            <div className={css.title}>
                <FileZipOutlined style={{color: grey[1]}} />
                <span>{"Archivierte Kurse"}</span>
            </div>
        );

        const footer = (
            <div className={css.footer}>
                <Button type="primary" onClick={() => setDescripitionModalOpen(false)}>
                    {"Schließen"}
                </Button>
            </div>
        );

        return (
            <>
                <InfoCircleOutlined style={{color: blue[4]}} onClick={() => setDescripitionModalOpen(!descripitionModalOpen)} />{" "}
                {descripitionModalOpen && (
                    <DraggableModal title={title} footer={footer} open={descripitionModalOpen} onCancel={() => setDescripitionModalOpen(false)}>
                        <p>{"Kurse können archiviert werden, um die Übersichtlichkeit zu verbessern."}</p>
                        <p>{"Eigenschaften:"}</p>
                        <ul>
                            <li>{"Archivierte Kurse werden im Web nicht angezeigt."}</li>
                            <li>{"Archivierte Kurse tauchen nicht mehr in der Kursliste auf."}</li>
                            <li>{"Buchungen von archivierten Kursen werden nicht mehr angezeigt."}</li>
                            <li>{"Im Zahlungsverkehr werden offene Posten und Umsätze von archivierten Kursen ebenfalls ausgeblendet. Der Export enthält weiterhin alle Posten."}</li>
                            <li>{"Jeder Benutzer des KursOrganizers kann selbst steuern, ob archivierte Kurse angezeigt werden sollen."}</li>
                        </ul>
                        <p>{"Archivierte Kurse können jederzeit wieder aktiviert werden."}</p>
                    </DraggableModal>
                )}
            </>
        );
    };

    return (
        <div className={css.moreMenuContainer}>
            {menuOpen && (
                <div className={courseIds && courseIds.length > 0 ? css.menuOpenContainer : classNames(css.menuOpenContainer, css.roundedCorners)}>
                    <DescripitionModal />
                    <ShowArchivedSwitcher />
                    {courseIds && courseIds.length > 0 && <ArchiveMultipleCourses />}
                </div>
            )}
            <MoreOutlined onClick={() => setMenuOpen(!menuOpen)} />
        </div>
    );
};

export default memo(CourseListShowMoreMenu);
